import type { GitStatusResult, LinkedPullRequest } from "@cmmd-forge/contracts";
import type { MouseEvent } from "react";
import { useMemo } from "react";

import {
  ThreadPullRequestBadgeControl,
  ThreadPullRequestsMiniList,
  resolveThreadPullRequestEntries,
  useLinkedThreadPullRequest,
  type PrStatusIndicator,
  type ThreadPullRequestEntry,
} from "../ThreadPullRequestIndicators";
import { prStatusIndicator, resolveThreadPr } from "./SidebarThreadRow.helpers";

// The badge clones its behavior onto this anchor: the href, the accessible
// name, and the click. A badge is a link, so the element is one.
const PR_BADGE_RENDER = (
  <a className="inline-flex size-4 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
);

/**
 * The thread's linked change request on a row's meta slot, as the host link a
 * badge is. A thread without a link renders nothing, which is what the control
 * underneath decides.
 */
export function SidebarThreadPullRequestBadge(props: {
  readonly badge: { readonly number: number | null; readonly url: string | null };
  readonly status: PrStatusIndicator | null;
  readonly onOpenPullRequest: (event: MouseEvent<HTMLElement>, url: string) => void;
}) {
  return (
    <ThreadPullRequestBadgeControl
      render={PR_BADGE_RENDER}
      number={props.badge.number ?? undefined}
      url={props.badge.url ?? undefined}
      status={props.status}
      onOpenPullRequest={(event) => {
        if (props.badge.url !== null) props.onOpenPullRequest(event, props.badge.url);
      }}
    />
  );
}

/** The thread's pull-request lines under the row title's hover tooltip. */
export function SidebarThreadPullRequestsTooltipSection(props: {
  readonly entries: ReadonlyArray<ThreadPullRequestEntry>;
}) {
  if (props.entries.length === 0) return null;
  return (
    <div className="mt-2 border-t border-border/60 pt-2 text-xs">
      <ThreadPullRequestsMiniList entries={props.entries} />
    </div>
  );
}

/**
 * Everything a row shows about its change requests, from the branch's own pull request plus
 * the thread's durable link: the merged-only lead status, the badge status, and the mini-list
 * entries. The badge reads the cache without subscribing, so a row never re-renders from a
 * listing tick.
 */
export function useSidebarThreadPullRequest(
  thread: { readonly linkedPullRequest?: LinkedPullRequest | null | undefined },
  displayBranch: string | null,
  gitStatus: GitStatusResult | null,
  linkedPrUrl: string | null,
): {
  readonly pr: ReturnType<typeof resolveThreadPr>;
  readonly prStatus: ReturnType<typeof prStatusIndicator>;
  readonly indicator: PrStatusIndicator | null;
  readonly entries: ReadonlyArray<ThreadPullRequestEntry>;
} {
  // Keyed on the branch the row DISPLAYS, not the one the record froze. `resolveThreadPr`
  // returns null unless the two agree, so keying it on a stale record is what silently removed
  // the PR chip from a drifted row: no error, no empty state, just an absent number.
  const pr = resolveThreadPr(displayBranch, gitStatus);
  const prStatus = prStatusIndicator(pr, {
    hasThreadLinkedPr: linkedPrUrl !== null && linkedPrUrl === pr?.url,
  });
  const { liveStatus, indicator } = useLinkedThreadPullRequest(thread.linkedPullRequest);
  const entries = useMemo(
    () =>
      resolveThreadPullRequestEntries(
        thread.linkedPullRequest,
        pr && pr.url !== ""
          ? { number: pr.number, url: pr.url, state: pr.state, title: pr.title }
          : null,
        liveStatus,
      ),
    [liveStatus, pr, thread.linkedPullRequest],
  );
  return { pr, prStatus, indicator, entries };
}
